import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import Loader from './Components/Loader';
import MapMapbox from './Components/MapMapbox';
import Controls from './Components/MapControls';
import Info from './Components/Info';
import Sidebar from './Components/Sidebar';
import Logos from './Components/Logos';
import HoverTooltip from './Components/HoverTooltip';
import Header from './Components/Header';
import Tips from './Components/Tips';
import Legend from './Components/Legend';
import CookieConsent from './Components/CookieConsent';

import { isMobile, isIframe } from './Utilities/Helpers';
import { getTranslation } from './Utilities/API';
import { LAYERS, LOCAL_STORAGE_TOKEN } from './Utilities/Config';
import { MapProvider } from './Services/MapContext';

import 'tippy.js/dist/tippy.css';
import './App.css';

export default function App() {
	// Map data
	const [selectedData, setSelectedData] = useState(null);

	// language
	const [language, setLanguage] = useState(null);

	// Iframe mode
	const [iframeMode, setIframeMode] = useState(isIframe());

	// Query search parameters
	const [searchParams, setSearchParams] = useSearchParams({}); // eslint-disable-line no-unused-vars

	// Windows/forms
	const wrapperRef = useRef(null);
	const [onMobile, setOnMobile] = useState(isMobile());
	const [loaderVisible, setLoaderVisible] = useState(true);
	const [infoVisible, setInfoVisible] = useState(false);
	const [sidebarVisible, setSidebarVisible] = useState(false);
	const [sidebarWidth, setSidebarWidth] = useState(0);
	const handleLoaderVisibility = (state) => { setLoaderVisible(state); }
	const handleInfoVisibility = () => { setInfoVisible((prevState) => !prevState); }
	const handleSidebarVisibility = (state) => { setSidebarVisible(state); }

	// list of layers
	const layersVisible = useRef(LAYERS);

	// Cookie disclaimer
	const [cookieDisclaimerAgreed, setCookieDisclaimerAgreed] = useState(false);
	const handleCookieDisclaimerAgreed = (state) => { setCookieDisclaimerAgreed(state); }

	// Hover tooltip
	const [hoverTooltipVisible, setHoverTooltipVisible] = useState(false);
	const [mousePosition, setMousePosition] = useState({ left: 0, top: 0 });
	const [tooltipContent, setTooltipContent] = useState({type: undefined, content: undefined});

	// Disclaimer with helpful tips
	const [tipsVisible, setTipsVisible] = useState(true);
	const handleTipsVisibility = (state) => { setTipsVisible(state); }
	useEffect(() => {
		const tipsStatus = JSON.parse(localStorage.getItem(LOCAL_STORAGE_TOKEN)) || false;
		if (tipsStatus === true) setTipsVisible(false);
	}, []);

	// Cookie Consent
	// eslint-disable-next-line no-unused-vars
	const [disclaimerAgreed, setDisclaimerAgreed] = useState(JSON.parse(localStorage.getItem('disclaimerAgreed')) || false);
	const inIframe = () => window.self !== window.top;

	useEffect(() => {
	 	localStorage.setItem('disclaimerAgreed', JSON.stringify(disclaimerAgreed));
	}, [disclaimerAgreed]);

	// get gsheet data
	useEffect(() => {
		getTranslation(json => {
			let translation = json.reduce((acc, entry) => {
				if (entry.param) acc[entry.param] = entry.value;
				return acc;
			}, {});
			setLanguage(translation);
		});

		if (searchParams) {
			const iframeParam = searchParams.get('iframe');
			if (iframeParam === 'true') setIframeMode(iframeParam === 'true' ? true : false);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Detect page resize
	useEffect(() => {
		if (!wrapperRef.current) return;
		const observer = new ResizeObserver((entries) => {
			if (entries.length > 0) {
				setOnMobile(isMobile());
			}
		});
		// Enable
		observer.observe(wrapperRef.current);
		// Cleanup function
		return () => { observer.disconnect(); };
	}, [setOnMobile]);

	return (
		<div className="App" ref={wrapperRef}>
			<Loader
				show={loaderVisible}
			/>

			<Header
				language={language}
			/>

			{!cookieDisclaimerAgreed ? <>
				<CookieConsent
					handleDisclaimerAgreed={handleCookieDisclaimerAgreed}
					setLoaderVisible={handleLoaderVisibility}
					language={language}
				/>
			</> : <>
				<MapProvider>
					<Controls
						onToggleInfo={handleInfoVisibility}
						setSelectedData={setSelectedData}
						language={language}
						onMobile={onMobile}
					/>
					<MapMapbox
						isEmbedded={inIframe()}
						layersVisible={layersVisible}
						selectedData={selectedData}
						setSelectedData={setSelectedData}
						toggleSidebar={handleSidebarVisibility}
						setLoaderVisible={handleLoaderVisibility}
						sidebarWidth={sidebarWidth}
						onMouseMove={(ev)=> setMousePosition(ev)}
						setTooltipContent={setTooltipContent}
						setHoverTooltipVisible={setHoverTooltipVisible}
						language={language}
						iframeMode={iframeMode}
					>
						<HoverTooltip
							data={tooltipContent}
							mousePosition={mousePosition}
							hoverTooltipVisible={hoverTooltipVisible}
							setHoverTooltipVisible={setHoverTooltipVisible}
							language={language}
						/>
					</MapMapbox>
				</MapProvider>
				<Tips
					disclaimerAgreed={disclaimerAgreed}
					show={tipsVisible}
					onToggle={handleTipsVisibility}
					language={language}
				/>
			</>}

			<Sidebar
				show={sidebarVisible}
				data={selectedData}
				onToggle={handleSidebarVisibility}
				setSelectedData={setSelectedData}
				setSidebarWidth={setSidebarWidth}
				language={language}
			/>

			<Legend
				show={true}
				language={language}
			/>
			
			<Info
				show={infoVisible}
				onToggle={handleInfoVisibility}
				language={language}
			/>
			{!inIframe() && <Logos language={language} />}
		</div>
	);
}